/**
 * @rogueworlds/client — the WebGL2 renderer, and the ONLY place that decides how the canvas is
 * created and colour-managed.
 *
 * Extracted from `docs/reference/threejs-game-conventions.md` (conventions 1-4): one factory owns
 * the context flags, the devicePixelRatio cap, the output colour space and the tone-mapping
 * pipeline, and resize is a pure function that reports whether anything actually changed. No other
 * module is allowed to set any of those, so quality cannot drift between modules.
 */

import * as THREE from "three";

/**
 * DPR cap. On a high-density display this is the cheapest quality lever there is, and post
 * processing cost scales with it (convention 3). `devicePixelRatio` is read at call time so a
 * window dragged to another monitor picks up the new value on the next resize check.
 */
const MAX_DEVICE_PIXEL_RATIO = 2;

/** The CSS size and DPR we last applied to a renderer. */
interface AppliedSize {
  readonly width: number;
  readonly height: number;
  readonly dpr: number;
}

/**
 * Per-renderer "what did we last apply" table. A WeakMap keeps `resizeRenderer` allocation-free on
 * the (overwhelmingly common) no-change path and drops the entry with the renderer.
 */
const appliedSizes = new WeakMap<THREE.WebGLRenderer, AppliedSize>();

/** Creates the WebGL2 renderer for one canvas. Context flags are frozen after this call. */
export function createRenderer(canvas: HTMLCanvasElement): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    stencil: false,
    powerPreference: "high-performance",
  });

  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO));

  // Explicit colour pipeline (convention 2): without it textured materials read back washed out
  // and the fog no longer matches the sky.
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;

  // One shadow-casting light maximum, PCF, frustum owned by scene.ts (convention 5). three r186 REMOVED
  // the soft variant: `PCFSoftShadowMap` is `@deprecated since r186` and `WebGLShadowMap.render` logs
  // "PCFSoftShadowMap has been removed. Using PCFShadowMap instead." on every render before substituting
  // `PCFShadowMap`. Setting the surviving constant is therefore the same rendering without the spam.
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;

  return renderer;
}

/**
 * Matches the renderer's drawing buffer to the container's client size and keeps the camera in
 * step. Returns true only when something changed, so the caller can invoke it every frame for free
 * (convention 4) — resizing the renderer without the camera is the classic stretched-image bug.
 */
export function resizeRenderer(
  renderer: THREE.WebGLRenderer,
  camera: THREE.PerspectiveCamera,
  container: HTMLElement,
): boolean {
  const width = container.clientWidth;
  const height = container.clientHeight;
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
  if (width <= 0 || height <= 0) {
    // A hidden or not-yet-laid-out container: keep the last good size instead of a 0x0 buffer.
    return false;
  }

  const previous = appliedSizes.get(renderer);
  if (previous !== undefined && previous.width === width && previous.height === height && previous.dpr === dpr) {
    return false;
  }

  renderer.setPixelRatio(dpr);
  // `updateStyle = false`: the container owns layout, the canvas is sized by CSS.
  renderer.setSize(width, height, false);
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  appliedSizes.set(renderer, { width, height, dpr });
  return true;
}

/** Releases the WebGL context, its programs, render targets and textures. */
export function disposeRenderer(renderer: THREE.WebGLRenderer): void {
  appliedSizes.delete(renderer);
  renderer.dispose();
  // Without this the driver keeps the context (and its GPU memory) for the life of the page.
  renderer.forceContextLoss();
}
